import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AppConstants } from '../constants/app-constants';

export type AppLanguage = 'en' | 'ar';

const SUPPORTED_LANGUAGES: AppLanguage[] = ['ar', 'en'];
const DEFAULT_LANGUAGE: AppLanguage = 'ar';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  constructor(private translate: TranslateService) { }

  init(): void {
    this.translate.addLangs(SUPPORTED_LANGUAGES);
    this.translate.setDefaultLang(DEFAULT_LANGUAGE);
    this.use(this.storedLanguage ?? DEFAULT_LANGUAGE);
  }

  get current(): AppLanguage {
    return this.normalize(this.translate.currentLang);
  }

  get alternate(): AppLanguage {
    return this.current === 'ar' ? 'en' : 'ar';
  }

  get direction(): 'rtl' | 'ltr' {
    return this.current === 'ar' ? 'rtl' : 'ltr';
  }

  toggle(): void {
    this.use(this.alternate);
  }

  use(language: AppLanguage): void {
    const next = this.normalize(language);
    localStorage.setItem(AppConstants.LANGUAGE, next);
    this.translate.use(next);
    this.applyDirection(next);
  }

  /** Roles come from the database, so fall back to the raw value when untranslated. */
  roleLabel(role: string | null | undefined): string {
    if (!role) {
      return '';
    }
    const key = `ROLES.${String(role).toUpperCase()}`;
    const label = this.translate.instant(key);
    return label === key ? String(role) : label;
  }

  private get storedLanguage(): AppLanguage | null {
    const saved = localStorage.getItem(AppConstants.LANGUAGE);
    return SUPPORTED_LANGUAGES.includes(saved as AppLanguage) ? (saved as AppLanguage) : null;
  }

  private normalize(language: string | null | undefined): AppLanguage {
    return SUPPORTED_LANGUAGES.includes(language as AppLanguage) ? (language as AppLanguage) : DEFAULT_LANGUAGE;
  }

  private applyDirection(language: AppLanguage): void {
    const direction = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', language);
  }
}
